import { useState, useRef, useEffect, TouchEvent, MouseEvent as ReactMouseEvent } from 'react';
import { cn } from '@/lib/utils';
import { MoveHorizontal } from 'lucide-react';
import { PictureImage } from '@/components/ui/picture-image';

interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeImageSmall?: string;
  afterImageSmall?: string;
  altText: string;
  className?: string;
  containerClassName?: string;
  caption?: string;
  compact?: boolean;
  narrowDesktop?: boolean;
  narrowDesktopColumns?: 2 | 3;
  sameImageVariants?: boolean;
  largeImageWidth?: number;
}

const TWO_COLUMN_NARROW_SIZES =
  "(max-width: 767px) 100vw, min(552px, 50vw)";
const THREE_COLUMN_NARROW_SIZES =
  "(max-width: 767px) 100vw, min(368px, 33.333vw)";

export function BeforeAfterSlider({ 
  beforeImage, 
  afterImage,
  beforeImageSmall,
  afterImageSmall,
  altText, 
  className,
  containerClassName,
  caption,
  compact = false,
  narrowDesktop = false,
  narrowDesktopColumns = 2,
  sameImageVariants = false,
  largeImageWidth = 1200,
}: BeforeAfterSliderProps) {
  const narrowDesktopSizes = narrowDesktop
    ? narrowDesktopColumns === 3
      ? THREE_COLUMN_NARROW_SIZES
      : TWO_COLUMN_NARROW_SIZES
    : undefined;
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = Math.max(0, Math.min((x / rect.width) * 100, 100));
    
    setSliderPosition(percentage);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove as any, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove as any);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div className={cn("flex flex-col gap-3", compact && "h-full", className)}>
      <div 
        ref={containerRef}
        className={cn(
          "relative w-full overflow-hidden select-none cursor-ew-resize group",
          compact ? "h-full rounded-none" : "aspect-[4/3] sm:aspect-[16/9] rounded-xl",
          containerClassName
        )}
        onMouseDown={(e: ReactMouseEvent) => {
          setIsDragging(true);
          handleMove(e.clientX);
        }}
        onTouchStart={(e: TouchEvent) => {
          setIsDragging(true);
          handleMove(e.touches[0].clientX);
        }}
      >
        {/* After Image (Background) */}
        <PictureImage
          src={afterImage}
          smallSrc={afterImageSmall}
          alt={`${altText} - After`}
          width={1200}
          height={900}
          loading="lazy"
          sameImageVariants={sameImageVariants}
          sizes={narrowDesktopSizes}
          largeVariantWidth={largeImageWidth}
          className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
        />

        {/* Before Image (Foreground, clipped) */}
        <PictureImage
          src={beforeImage}
          smallSrc={beforeImageSmall}
          alt={`${altText} - Before`}
          width={1200}
          height={900}
          loading="lazy"
          sameImageVariants={sameImageVariants}
          sizes={narrowDesktopSizes}
          largeVariantWidth={largeImageWidth}
          className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        />

        {/* Slider Handle */}
        <div 
          className="absolute inset-y-0 w-1 bg-white cursor-ew-resize shadow-[0_0_10px_rgba(0,0,0,0.3)] transform -translate-x-1/2"
          style={{ left: `${sliderPosition}%` }}
        >
          <div className={cn(
            "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full shadow-lg flex items-center justify-center transition-transform group-hover:scale-110 text-[#1a3a1a]",
            compact ? "w-7 h-7" : "w-10 h-10"
          )}>
            <MoveHorizontal className={compact ? "w-4 h-4" : "w-6 h-6"} />
          </div>
        </div>

        {/* Labels */}
        <div className={cn(
          "absolute left-2 bg-black/60 text-white rounded font-medium backdrop-blur-sm pointer-events-none transition-opacity duration-300",
          compact ? "top-2 px-2 py-0.5 text-xs" : "top-4 px-3 py-1 text-sm"
        )} style={{ opacity: sliderPosition > 15 ? 1 : 0 }}>
          Before
        </div>
        <div className={cn(
          "absolute right-2 bg-[#c84060]/90 text-white rounded font-medium backdrop-blur-sm pointer-events-none transition-opacity duration-300",
          compact ? "top-2 px-2 py-0.5 text-xs" : "top-4 px-3 py-1 text-sm"
        )} style={{ opacity: sliderPosition < 85 ? 1 : 0 }}>
          After
        </div>
      </div>
      
      {caption && (
        <p className="text-center text-sm font-medium text-white/90 italic">
          {caption}
        </p>
      )}
    </div>
  );
}
